'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { AuctionItem } from '@/app/components/AuctionDetailModal';
import { favoritesApi, ApiError } from '@/lib/api';
import { useAuth } from './AuthContext';
import { beAuctionToAuctionItem } from '@/lib/api/adapters';

const STORAGE_KEY = 'metricaleiloes-favorites';

interface FavoritesContextValue {
  favorites: AuctionItem[];
  isFavorite: (id: string) => boolean;
  toggleFavorite: (auction: AuctionItem) => Promise<void>;
  removeFavorite: (id: string) => Promise<void>;
  count: number;
  isSynced: boolean;
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

function localLoad(): AuctionItem[] {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    return s ? JSON.parse(s) : [];
  } catch {
    return [];
  }
}

function localSave(items: AuctionItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* noop */
  }
}

export function FavoritesProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isOnline } = useAuth();
  const [favorites, setFavorites] = useState<AuctionItem[]>([]);
  const [isSynced, setIsSynced] = useState(false);

  // Carregar: BE se autenticado, localStorage caso contrário
  useEffect(() => {
    if (isAuthenticated && isOnline) {
      favoritesApi
        .list()
        .then((beFavs) => {
          const items = beFavs.map((f) => beAuctionToAuctionItem(f.auction));
          setFavorites(items);
          localSave(items);
          setIsSynced(true);
        })
        .catch(() => {
          setFavorites(localLoad());
        });
    } else {
      setFavorites(localLoad());
      setIsSynced(false);
    }
  }, [isAuthenticated, isOnline]);

  const isFavorite = useCallback((id: string) => favorites.some((f) => f.id === id), [favorites]);

  const toggleFavorite = useCallback(
    async (auction: AuctionItem) => {
      const exists = isFavorite(auction.id);

      // Otimismo: atualiza local imediatamente
      const next = exists ? favorites.filter((f) => f.id !== auction.id) : [auction, ...favorites];
      setFavorites(next);
      localSave(next);

      if (isAuthenticated && isOnline) {
        try {
          if (exists) {
            // Se for um item do BE, o auctionId real está no id
            const beId = auction.id.replace('scraped-', '').replace(/^[^-]+-[^-]+-/, '');
            await favoritesApi.remove(auction.beId ?? auction.id);
          } else {
            if (auction.beId) {
              await favoritesApi.add(auction.beId);
            }
            // Se não tem beId (item mock), só salva local
          }
        } catch (e) {
          if (e instanceof ApiError) {
            // Reverte em caso de erro
            setFavorites(favorites);
            localSave(favorites);
          }
        }
      }
    },
    [favorites, isAuthenticated, isOnline, isFavorite]
  );

  const removeFavorite = useCallback(
    async (id: string) => {
      const prev = favorites;
      const next = favorites.filter((f) => f.id !== id);
      setFavorites(next);
      localSave(next);

      if (isAuthenticated && isOnline) {
        const item = prev.find((f) => f.id === id);
        if (item?.beId) {
          try {
            await favoritesApi.remove(item.beId);
          } catch {
            setFavorites(prev);
            localSave(prev);
          }
        }
      }
    },
    [favorites, isAuthenticated, isOnline]
  );

  return (
    <FavoritesContext.Provider
      value={{
        favorites,
        isFavorite,
        toggleFavorite,
        removeFavorite,
        count: favorites.length,
        isSynced,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavorites must be used inside FavoritesProvider');
  return ctx;
}
